import { IVisibilityCapture } from '../../domain/interfaces/IVisibilityCapture';
import { LogEntry } from '../../domain/types';

export class VisibilityCapture implements IVisibilityCapture {
  private callback: ((entry: LogEntry) => void) | null = null;
  private isActive = false;
  private lastVisibility = true;

  start(callback: (entry: LogEntry) => void): void {
    if (this.isActive) return;
    
    this.callback = callback;
    this.isActive = true;
    this.lastVisibility = !document.hidden;
    
    this.setupListeners();
  }

  stop(): void {
    if (!this.isActive) return;
    
    this.removeListeners();
    this.callback = null;
    this.isActive = false;
  }

  private setupListeners(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private removeListeners(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleFocus);
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  private handleVisibilityChange = (): void => {
    if (!this.callback) return;
    
    const isHidden = document.hidden;
    
    if (isHidden !== !this.lastVisibility) {
      this.callback({
        level: 'info',
        msg: isHidden ? 'Tab hidden' : 'Tab visible',
        ts: new Date().toISOString(),
        meta: {
          type: 'visibility',
          visibilityEvent: isHidden ? 'hide' : 'show',
          url: window.location.href
        }
      });
      this.lastVisibility = !isHidden;
    }
  };

  private handleFocus = (): void => {
    if (!this.callback) return;
    
    this.callback({
      level: 'info',
      msg: 'Window focused',
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'visibility',
        visibilityEvent: 'focus',
        url: window.location.href
      }
    });
  };

  private handleBlur = (): void => {
    if (!this.callback) return;
    
    this.callback({
      level: 'info',
      msg: 'Window blurred',
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'visibility',
        visibilityEvent: 'blur',
        url: window.location.href
      }
    });
  };

  private handleBeforeUnload = (): void => {
    if (!this.callback) return;
    
    this.callback({
      level: 'info',
      msg: 'Page unloading',
      ts: new Date().toISOString(),
      source: 'browser',
      meta: {
        type: 'visibility',
        visibilityEvent: 'beforeunload',
        url: window.location.href
      }
    });
  };
}